import type { IGame, InputAction, GameState } from "../../core/types";
import { clamp } from "../../core/Time";

type Obstacle = { x: number; w: number; h: number; passed: boolean };

export class RunnerGame implements IGame {
  private state: GameState = { status: "ready" };

  private score = 0;
  private t = 0;

  private groundY = 0;
  private viewportW = 0;
  private viewportH = 0;

  private player = {
    x: 140,
    y: 0,
    w: 26,
    h: 34,
    vy: 0,
    onGround: true
  };

  private speed = 360;     // vitesse du monde
  private gravity = 2200;  // gravité
  private jumpV = -760;    // impulsion saut

  private spawnT = 0;
  private obstacles: Obstacle[] = [];

  reset(): void {
    this.state = { status: "ready" };
    this.score = 0;
    this.t = 0;
    this.speed = 360;
    this.spawnT = 0;
    this.obstacles = [];
    this.player.vy = 0;
    this.player.onGround = true;
  }

  getState(): GameState {
    if (this.state.status === "running") return { status: "running", score: this.score };
    if (this.state.status === "gameover") return this.state;
    return { status: "ready" };
  }

  onAction(action: InputAction): void {
    const isJump =
      action.type === "tap" ||
      (action.type === "keydown" && action.key === " ");

    if (!isJump) return;

    if (this.state.status === "ready") {
      this.start();
      return;
    }

    if (this.state.status === "gameover") {
      this.reset();
      this.start();
      return;
    }

    if (this.state.status === "running") {
      this.jump();
    }
  }

  private start() {
    this.state = { status: "running", score: 0 };
    // position initiale
    this.player.x = 140;
    this.player.y = this.groundY - this.player.h;
    this.player.vy = 0;
    this.player.onGround = true;
  }

  private jump() {
    if (!this.player.onGround) return;
    this.player.vy = this.jumpV;
    this.player.onGround = false;
  }

  update(dt: number): void {
    if (this.state.status !== "running") return;

    // si pas encore de taille (premier render pas passé), on attend
    if (this.viewportW <= 0 || this.viewportH <= 0) return;

    this.t += dt;
    this.speed += 18 * dt; // accélération progressive
    this.score = Math.floor(this.t * 10);

    // spawn obstacles
    this.spawnT -= dt;
    const every = clamp(1.0 - this.t * 0.015, 0.45, 1.0);
    if (this.spawnT <= 0) {
      this.spawnT = every;
      this.spawnObstacle();
    }

    // physics player
    this.player.vy += this.gravity * dt;
    this.player.y += this.player.vy * dt;

    const floorY = this.groundY - this.player.h;
    if (this.player.y >= floorY) {
      this.player.y = floorY;
      this.player.vy = 0;
      this.player.onGround = true;
    }

    // move obstacles + collision
    const keep: Obstacle[] = [];
    const px1 = this.player.x;
    const py1 = this.player.y;
    const px2 = this.player.x + this.player.w;
    const py2 = this.player.y + this.player.h;

    for (const o of this.obstacles) {
      o.x -= this.speed * dt;

      // score bonus quand passé
      if (!o.passed && o.x + o.w < this.player.x) {
        o.passed = true;
        this.score += 5;
      }

      // collision AABB
      const ox1 = o.x;
      const oy1 = this.groundY - o.h;
      const ox2 = o.x + o.w;
      const oy2 = this.groundY;

      const hit =
        px1 < ox2 && px2 > ox1 &&
        py1 < oy2 && py2 > oy1;

      if (hit) {
        this.state = { status: "gameover", score: this.score, best: 0 };
      }

      if (o.x > -200) keep.push(o);
    }
    this.obstacles = keep;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    this.viewportW = w;
    this.viewportH = h;

    // sol à ~75% de la hauteur
    this.groundY = Math.floor(h * 0.78);

    // initialisation y si ready
    if (this.state.status === "ready") {
      this.player.y = this.groundY - this.player.h;
    }

    ctx.clearRect(0, 0, w, h);

    // background
    ctx.fillStyle = "rgba(255,255,255,0.03)";
    ctx.fillRect(0, 0, w, h);

    // sol
    ctx.strokeStyle = "rgba(255,255,255,0.14)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, this.groundY);
    ctx.lineTo(w, this.groundY);
    ctx.stroke();

    // obstacles
    for (const o of this.obstacles) {
      ctx.fillStyle = "rgba(239,68,68,0.85)";
      ctx.fillRect(o.x, this.groundY - o.h, o.w, o.h);
    }

    // player (simple rectangle)
    ctx.fillStyle = "rgba(34,197,94,0.90)";
    ctx.fillRect(this.player.x, this.player.y, this.player.w, this.player.h);

    // hint
    ctx.fillStyle = "rgba(229,231,235,0.85)";
    ctx.font = "14px system-ui";
    if (this.state.status === "ready") {
      ctx.fillText("Espace / clic pour démarrer — puis sauter", 24, h - 24);
    } else if (this.state.status === "gameover") {
      ctx.fillText("Game Over — Espace / clic pour rejouer", 24, h - 24);
    }
  }

  private spawnObstacle() {
    const w = this.viewportW || 900;
    const h = 24 + Math.random() * 34;  // hauteur obstacle
    const ow = 18 + Math.random() * 28; // largeur obstacle
    this.obstacles.push({
      x: w + 40,
      w: ow,
      h: h,
      passed: false
    });
  }
}
